/**
 * ResponsiveTable Component
 * Desktop: Traditional table layout
 * Mobile: Card layout with all data visible
 *
 * Features:
 * - Automatic responsive transformation
 * - Row selection support
 * - Custom card rendering
 * - Touch-friendly interactions
 */

import { ReactNode } from 'react';
import { useIsMobileOrTablet } from '../../hooks/useMediaQuery';
import { cn } from '../../utils/responsive';

export interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  className?: string;
  mobileHidden?: boolean; // Hide this column on mobile
}

export interface ResponsiveTableProps<T> {
  data: T[];
  columns: Column<T>[];
  renderCard?: (item: T, index: number) => ReactNode; // Custom card rendering for mobile
  onRowClick?: (item: T) => void;
  selectable?: boolean;
  selectedIds?: Set<string | number>;
  onSelect?: (id: string | number) => void;
  onSelectAll?: (selected: boolean) => void;
  getItemId: (item: T) => string | number;
  className?: string;
  emptyMessage?: string;
  loading?: boolean;
}

export function ResponsiveTable<T>({
  data,
  columns,
  renderCard,
  onRowClick,
  selectable = false,
  selectedIds = new Set(),
  onSelect,
  onSelectAll,
  getItemId,
  className,
  emptyMessage = 'ไม่พบข้อมูล',
  loading = false,
}: ResponsiveTableProps<T>) {
  const isMobileOrTablet = useIsMobileOrTablet();

  // Handle select all
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll?.(e.target.checked);
  };

  // Handle individual selection
  const handleSelect = (id: string | number) => {
    onSelect?.(id);
  };

  // Check if all items are selected
  const allSelected = data.length > 0 && data.every(item => selectedIds.has(getItemId(item)));
  const someSelected = data.some(item => selectedIds.has(getItemId(item))) && !allSelected;

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-stone-100 dark:bg-gray-800 rounded-lg shimmer" />
        ))}
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-stone-200 dark:border-gray-700 p-12 text-center">
        <p className="text-stone-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  // Mobile: Card Layout
  if (isMobileOrTablet) {
    return (
      <div className={cn('space-y-3', className)}>
        {data.map((item, index) => {
          const itemId = getItemId(item);
          const isSelected = selectedIds.has(itemId);

          // Use custom card renderer if provided
          if (renderCard) {
            return (
              <div
                key={itemId}
                className={cn(
                  'relative',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick?.(item)}
              >
                {selectable && (
                  <div
                    className="absolute top-4 left-4 z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(itemId)}
                      className="w-5 h-5 rounded border-stone-300 text-yellow-500
                               focus:ring-yellow-500 focus:ring-offset-0 touch-target"
                    />
                  </div>
                )}
                {renderCard(item, index)}
              </div>
            );
          }

          // Default card rendering
          return (
            <div
              key={itemId}
              className={cn(
                'bg-white dark:bg-gray-800 rounded-lg border border-stone-200 dark:border-gray-700 p-4',
                'hover:border-yellow-400 hover:shadow-accent',
                'transition-all duration-200',
                onRowClick && 'cursor-pointer active:scale-[0.99]',
                isSelected && 'border-yellow-400 bg-yellow-50/30 dark:bg-yellow-900/20'
              )}
              onClick={() => onRowClick?.(item)}
            >
              <div className="flex items-start gap-3">
                {selectable && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(itemId)}
                      className="mt-1 w-5 h-5 rounded border-stone-300 dark:border-gray-600 text-yellow-500
                               focus:ring-yellow-500 focus:ring-offset-0 touch-target"
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  {columns
                    .filter(col => !col.mobileHidden)
                    .map(col => (
                      <div key={col.key}>
                        <dt className="text-xs font-medium text-stone-500 dark:text-gray-400 mb-0.5">
                          {col.header}
                        </dt>
                        <dd className="text-sm text-stone-900 dark:text-gray-100">
                          {col.render(item)}
                        </dd>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop: Table Layout
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="table-hyb">
        <thead>
          <tr>
            {selectable && (
              <th className="w-12">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-stone-300 dark:border-gray-600 text-yellow-500
                           focus:ring-yellow-500 focus:ring-offset-0"
                />
              </th>
            )}
            {columns.map(col => (
              <th key={col.key} className={col.className}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, _index) => {
            const itemId = getItemId(item);
            const isSelected = selectedIds.has(itemId);

            return (
              <tr
                key={itemId}
                className={cn(
                  onRowClick && 'cursor-pointer',
                  isSelected && 'bg-yellow-50/30 dark:bg-yellow-900/20'
                )}
                onClick={() => onRowClick?.(item)}
              >
                {selectable && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(itemId)}
                      className="w-4 h-4 rounded border-stone-300 dark:border-gray-600 text-yellow-500
                               focus:ring-yellow-500 focus:ring-offset-0"
                    />
                  </td>
                )}
                {columns.map(col => (
                  <td key={col.key} className={col.className}>
                    {col.render(item)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
